/**
 * ScormViewer — plays SCORM 1.2 and xAPI (TinCan) packages inside an iframe.
 *
 * SCORM 1.2: installs the window.API adapter (LMSInitialize/LMSGetValue/
 * LMSSetValue/LMSCommit/LMSFinish) that package JS discovers by walking
 * parent frames. CMI data is committed to the backend on LMSCommit/LMSFinish,
 * every 15s while dirty, and on unmount.
 *
 * xAPI: launches with TinCan query params (endpoint/auth/actor/activity_id/
 * registration) pointing at Lumyn's built-in mini-LRS. Completion arrives
 * via statements server-side; the viewer polls the rollup to reflect it.
 */
import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Package, RefreshCw } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const CMI_DEFAULTS = (launch) => ({
  'cmi.core.student_id': launch.learner_id || '',
  'cmi.core.student_name': launch.learner_name || 'Learner',
  'cmi.core.lesson_location': '',
  'cmi.core.credit': 'credit',
  'cmi.core.lesson_status': 'not attempted',
  'cmi.core.entry': 'ab-initio',
  'cmi.core.lesson_mode': 'normal',
  'cmi.core.exit': '',
  'cmi.core.session_time': '00:00:00',
  'cmi.core.total_time': '0000:00:00.00',
  'cmi.core.score.raw': '',
  'cmi.core.score.min': '',
  'cmi.core.score.max': '',
  'cmi.suspend_data': '',
  'cmi.launch_data': '',
  'cmi.comments': '',
  'cmi.student_data.mastery_score': '',
  'cmi.core._children': 'student_id,student_name,lesson_location,credit,lesson_status,entry,score,total_time,lesson_mode,exit,session_time',
  'cmi.core.score._children': 'raw,min,max',
});

export function ScormViewer({ item, assignmentId, onProgress }) {
  const { get, post } = useApi();
  const [launch, setLaunch] = useState(null);
  const [error, setError] = useState(null);
  const [apiReady, setApiReady] = useState(false);
  const cmiRef = useRef({});
  const dirtyRef = useRef(false);
  const completedRef = useRef(item.progress?.completed || false);

  useEffect(() => {
    get(`/me/learning/${assignmentId}/items/${item.item_id}/scorm/launch`)
      .then(setLaunch)
      .catch(e => setError(e.response?.data?.detail || 'Failed to launch package'));
  }, [get, assignmentId, item.item_id]);

  // ── SCORM 1.2: window.API adapter + commit loop ──
  useEffect(() => {
    if (!launch || launch.package_type !== 'scorm12') return undefined;
    const cmi = { ...CMI_DEFAULTS(launch), ...(launch.cmi || {}) };
    if (cmi['cmi.suspend_data'] || cmi['cmi.core.lesson_location']) {
      cmi['cmi.core.entry'] = 'resume';
    }
    cmiRef.current = cmi;

    const commit = async () => {
      if (!dirtyRef.current) return;
      dirtyRef.current = false;
      try {
        const res = await post(
          `/me/learning/${assignmentId}/items/${item.item_id}/scorm/commit`,
          { cmi: cmiRef.current },
        );
        if (res.completed && !completedRef.current) {
          completedRef.current = true;
          onProgress({});
        }
      } catch { /* retried on next dirty commit */ }
    };

    window.API = {
      LMSInitialize: () => 'true',
      LMSFinish: () => { commit(); return 'true'; },
      LMSGetValue: (k) => (cmiRef.current[k] !== undefined ? String(cmiRef.current[k]) : ''),
      LMSSetValue: (k, v) => { cmiRef.current[k] = String(v); dirtyRef.current = true; return 'true'; },
      LMSCommit: () => { commit(); return 'true'; },
      LMSGetLastError: () => '0',
      LMSGetErrorString: () => 'No error',
      LMSGetDiagnostic: () => '',
    };
    setApiReady(true);

    const iv = setInterval(commit, 15000);
    return () => {
      clearInterval(iv);
      commit();
      delete window.API;
      setApiReady(false);
    };
  }, [launch, assignmentId, item.item_id, post, onProgress]);

  // ── xAPI: poll the rollup so LRS-side completion reaches the UI ──
  useEffect(() => {
    if (!launch || launch.package_type !== 'xapi') return undefined;
    const iv = setInterval(() => {
      if (!completedRef.current) onProgress({});
    }, 20000);
    return () => clearInterval(iv);
  }, [launch, onProgress]);

  useEffect(() => {
    completedRef.current = item.progress?.completed || false;
  }, [item.progress?.completed]);

  if (error) {
    return <Card className="p-8 text-center text-sm text-rose-600" data-testid="scorm-error">{error}</Card>;
  }
  if (!launch) {
    return <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="scorm-loading">Loading course…</Card>;
  }

  let src = launch.content_url;
  if (launch.package_type === 'xapi') {
    const qs = new URLSearchParams({
      endpoint: `${window.location.origin}/api/v1/xapi`,
      auth: `Basic ${btoa(`${launch.launch_token}:`)}`,
      actor: JSON.stringify(launch.actor),
      activity_id: launch.activity_id,
      registration: launch.registration,
    });
    src = `${launch.content_url}${launch.content_url.includes('?') ? '&' : '?'}${qs}`;
  } else if (!apiReady) {
    return <Card className="p-8 text-center text-sm text-muted-foreground">Preparing player…</Card>;
  }

  return (
    <div data-testid="scorm-viewer">
      <div className="flex items-center gap-2 mb-2">
        <Package className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-medium truncate">{launch.title || item.title}</span>
        <Badge variant="outline" className="text-[10px] uppercase">
          {launch.package_type === 'xapi' ? 'xAPI' : 'SCORM 1.2'}
        </Badge>
        {item.progress?.completed && (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-700 ml-auto">
            <CheckCircle2 className="w-3.5 h-3.5" /> Completed
          </span>
        )}
      </div>
      <iframe
        title={launch.title || 'SCORM course'}
        src={src}
        className="w-full h-[70vh] rounded-lg border bg-white"
        allowFullScreen
        data-testid="scorm-iframe"
      />
      <div className="flex items-center gap-3 mt-2">
        <p className="text-xs text-muted-foreground flex-1">
          {launch.package_type === 'xapi'
            ? 'Your activity is tracked automatically as you learn.'
            : 'Your progress is saved automatically — resume anytime.'}
        </p>
        {launch.package_type === 'xapi' && !item.progress?.completed && (
          <Button size="sm" variant="ghost" onClick={() => onProgress({})}
                  data-testid="scorm-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh status
          </Button>
        )}
        {!item.progress?.completed && (
          <Button size="sm" variant="outline"
                  onClick={() => onProgress({ mark_complete: true })}
                  data-testid="scorm-mark-complete-btn">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark as completed
          </Button>
        )}
      </div>
    </div>
  );
}
